import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CustomerApiError, customerApi } from '../customer_api';
import type { BrandResponse } from '../lib/brand';

export function Landing() {
  const [brand, setBrand] = useState<BrandResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    customerApi<BrandResponse>('/api/customer/branding')
      .then(setBrand)
      .catch((e) => {
        if (e instanceof CustomerApiError && e.status === 404) {
          setError('not_found');
        } else {
          setError('network');
        }
      });
  }, []);

  if (error === 'not_found') {
    return (
      <main
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.5rem',
          textAlign: 'center',
        }}
      >
        <div>
          <h1 style={{ fontWeight: 500, fontSize: '1.4rem' }}>Not found</h1>
          <p style={{ color: '#6b6b6b' }}>This kennel does not exist.</p>
        </div>
      </main>
    );
  }

  if (!brand) {
    return (
      <main
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ color: '#6b6b6b' }}>Loading…</span>
      </main>
    );
  }

  const primary = brand.brand.primaryColour ?? '#004AAD';
  const tagline = brand.brand.tagline ?? `Boarding, daycare and grooming at ${brand.accountName}`;
  const pitch = brand.brand.pitchParagraph ?? null;

  return (
    <main style={{ minHeight: '100dvh', background: '#fafaf7' }}>
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.25rem 2rem',
          borderBottom: '1px solid #e5e5dd',
        }}
      >
        <span style={{ fontWeight: 600, letterSpacing: '-0.01em' }}>{brand.accountName}</span>
        <Link to="/sign-in" style={{ color: primary, fontSize: '0.875rem' }}>
          Sign in
        </Link>
      </header>

      <section style={{ maxWidth: 980, margin: '0 auto', padding: '5rem 2rem 4rem' }}>
        <h1
          style={{
            fontSize: 'clamp(2rem, 5vw, 3.25rem)',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            margin: 0,
            lineHeight: 1.05,
          }}
        >
          {brand.accountName}
        </h1>
        <p style={{ marginTop: '1rem', fontSize: '1.125rem', color: '#5a5a55', maxWidth: 620 }}>
          {tagline}
        </p>
        {pitch ? (
          <p style={{ marginTop: '1.25rem', maxWidth: 620, lineHeight: 1.6 }}>{pitch}</p>
        ) : null}
        <div style={{ marginTop: '2.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
          <Link
            to="/sign-in?next=/book"
            style={{
              display: 'inline-block',
              padding: '0.75rem 1.5rem',
              borderRadius: 10,
              background: primary,
              color: 'white',
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            Book a stay
          </Link>
          <Link
            to="/sign-in"
            style={{
              display: 'inline-block',
              padding: '0.75rem 1.5rem',
              borderRadius: 10,
              background: 'white',
              color: primary,
              border: `1px solid ${primary}`,
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            Sign in to manage
          </Link>
        </div>
      </section>

      {brand.brand.whyUs.length > 0 ? (
        <section
          style={{
            maxWidth: 980,
            margin: '0 auto',
            padding: '2rem 2rem 4rem',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1.5rem',
          }}
        >
          {brand.brand.whyUs.map((b, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static content, no reorder
            <article
              key={i}
              style={{
                padding: '1.5rem',
                borderRadius: 14,
                background: 'white',
                boxShadow: 'inset 0 0 0 1px #e5e5dd',
              }}
            >
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 500 }}>{b.title}</h3>
              <p style={{ marginTop: '0.6rem', color: '#5a5a55', lineHeight: 1.5 }}>{b.body}</p>
            </article>
          ))}
        </section>
      ) : null}

      <footer
        style={{
          borderTop: '1px solid #e5e5dd',
          padding: '2rem',
          textAlign: 'center',
          fontSize: '0.8rem',
          color: '#8a8a85',
        }}
      >
        {brand.publicPhone && (
          <div style={{ marginBottom: '0.5rem' }}>
            <a
              href={`tel:${brand.publicPhone}`}
              style={{ color: '#5a5a55', textDecoration: 'none', fontWeight: 500 }}
            >
              {brand.publicPhone}
            </a>
          </div>
        )}
        Powered by Kennel
      </footer>
    </main>
  );
}
